import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { SettingsValues } from '@shared/constants/settings-values';
import { AdditionalCoverageSlugs, WarehouseSlugs, WebsitePricingSlugs } from '@shared/constants/website-settings-slugs.type';
import { PricingModel } from '@shared/enums/pricing-model.enum';
import { StorageChargeType, StorageType } from '@shared/enums/storage-type.enum';
import { ConfigureSidemarkPricingDialogData, CustomWebsiteSettingsView, ProjectSettingFormFieldConfig, SidemarkPricingConfig, StorageAgreementHttpError } from '@shared/models/domain/custom-views.model';
import { ProjectSettingsView, ProjectView } from '@shared/models/domain/domain-view.model';
import { ProjectSetting } from '@shared/models/domain/wms.model';
import { BulkRequest } from '@shared/models/request/bulk-request.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { AlertService } from '@shared/services/alert.service';
import { ProjectSettingsService, ProjectSettingsViewService } from '@shared/services/project-settings.service';
import { WebsiteSettingsViewService } from '@shared/services/settings.service';
import { NgxMaskDirective } from 'ngx-mask';
import { finalize, forkJoin, switchMap } from 'rxjs';

type ProjectSettingControlValue = boolean | number | string | string[] | null;

@Component({
    selector: 'app-project-setting-form',
    imports: [
        NgTemplateOutlet,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatIcon,
        MatPrefix,
        NgxMaskDirective,
        MatInput,
        MatError,
        MatButton,
    ],
    templateUrl: './project-setting-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectSettingFormComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly alertService = inject(AlertService);
    private readonly projectSettingsService = inject(ProjectSettingsService);
    private readonly projectSettingsViewService = inject(ProjectSettingsViewService);
    private readonly websiteSettingsViewService = inject(WebsiteSettingsViewService);
    private readonly dialogData = inject<ConfigureSidemarkPricingDialogData | null>(MAT_DIALOG_DATA, { optional: true });
    
    isDialogSetting = input(false);
    projectView = input<ProjectView | null>(null);
    
    private readonly warehouseDeliveryFieldSlugs = new Set<string>([WebsitePricingSlugs.BasePrice, WebsitePricingSlugs.PricePerMinute, WarehouseSlugs.FieldDeliveryHourlyFee, WarehouseSlugs.CertificateFee]);
    private readonly squareFeetFieldSlugs = new Set<string>([WarehouseSlugs.PricePerSqrtFt, WarehouseSlugs.DisposalFeeSquare, WarehouseSlugs.DepartureFeeSquare, WarehouseSlugs.TrashFeeSquare]);
    private readonly cubicFeetFieldSlugs = new Set<string>([WarehouseSlugs.PricePerCubicFt, WarehouseSlugs.DisposalFeeCubic, WarehouseSlugs.DepartureFeeCubic, WarehouseSlugs.TrashFeeCubic]);
    private readonly cubicStorageTypes = new Set([StorageType.CubicFeetStorage, StorageType.CubicFeet]);

    private projectId = signal<string | null>(null);
    private projectSettings = signal<ProjectSettingsView[]>([]);
    private originalFormValues = signal<Record<string, number | null>>({});
    readonly isLoading = signal(false);
    readonly formFields = signal<ProjectSettingFormFieldConfig[]>([
        // General
        { controlName: 'warehouseCoverageFee', label: 'Warehouse Coverage Fee', slug: AdditionalCoverageSlugs.WarehouseCoverageFee, icon: 'feather:percent', type: 'percent', section: 'general', validators: [Validators.required, Validators.min(0), Validators.max(100)] },
        { controlName: 'warehouseCoverageFeeOneTime', label: 'Coverage Fee (%) - One Time', slug: AdditionalCoverageSlugs.WarehouseCoverageFeeOneTime, icon: 'feather:percent', type: 'percent', section: 'general', validators: [Validators.required, Validators.min(0), Validators.max(100)] },
        { controlName: 'wsFieldDeliveryHourlyFee', label: 'Field Delivery Hourly Fee', slug: WarehouseSlugs.FieldDeliveryHourlyFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'general', validators: [Validators.required] },
        { controlName: 'wsCertificateFee', label: 'COI (Certificate of Insurance) Fee', slug: WarehouseSlugs.CertificateFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'general', validators: [Validators.required] },

        // Per Service
        { controlName: 'wsReceivingFee', label: 'Warehouse Receiving Fee', slug: WarehouseSlugs.ReceivingFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService', validators: [Validators.required] },
        { controlName: 'wsInspectionFee', label: 'Warehouse Inspection Fee', slug: WarehouseSlugs.InspectionFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService', validators: [Validators.required] },
        { controlName: 'wsWillCallFee', label: 'Warehouse Will Call Fee', slug: WarehouseSlugs.WillCallFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsWillCallAdditionalFee', label: 'Warehouse Will Call Additional Fee', slug: WarehouseSlugs.WillCallAdditionalFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsDepartureChargeItem', label: 'Warehouse Departure Charge Item', slug: WarehouseSlugs.DepartureChargeItem, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsPricePerSqrtFt', label: 'Warehouse Price Per ft²', slug: WarehouseSlugs.PricePerSqrtFt, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsPricePerCubicFt', label: 'Warehouse Price Per ft³', slug: WarehouseSlugs.PricePerCubicFt, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsDisposalFeeSquare', label: 'Warehouse Disposal Fee ft²', slug: WarehouseSlugs.DisposalFeeSquare, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsDisposalFeeCubic', label: 'Warehouse Disposal Fee ft³', slug: WarehouseSlugs.DisposalFeeCubic, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsDepartureFeeSquare', label: 'Warehouse Departure Fee ft²', slug: WarehouseSlugs.DepartureFeeSquare, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsDepartureFeeCubic', label: 'Warehouse Departure Fee ft³', slug: WarehouseSlugs.DepartureFeeCubic, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsTrashFeeSquare', label: 'Warehouse Trash Fee ft²', slug: WarehouseSlugs.TrashFeeSquare, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsTrashFeeCubic', label: 'Warehouse Trash Fee ft³', slug: WarehouseSlugs.TrashFeeCubic, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService' },
        { controlName: 'wsLaborHourlyFee', label: 'Warehouse Labor Hourly Fee', slug: WarehouseSlugs.LaborHourlyFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService', validators: [Validators.required] },
        { controlName: 'wsExtraReceivingFee', label: 'Bulky / Crated / Rug Fee', slug: WarehouseSlugs.ExtraReceivingFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService', validators: [Validators.required] },
        { controlName: 'basePrice', label: 'Local Delivery (2 Movers)', slug: WebsitePricingSlugs.BasePrice, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService', pricingModel: PricingModel.baseRatePlusPerMinLastWayPoint, validators: [Validators.required] },
        { controlName: 'pricePerMinute', label: 'White-Glove Installation', slug: WebsitePricingSlugs.PricePerMinute, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'perService', pricingModel: PricingModel.baseRatePlusPerMinLastWayPoint, validators: [Validators.required] },

        // All Inclusive
        { controlName: 'wsAllInclusiveFinalMilePricePerCubicFt', label: 'All Inclusive Final Mile Price Per ft³', slug: WarehouseSlugs.AllInclusiveFinalMilePricePerCubicFt, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'allInclusive' },
        { controlName: 'wsAllInclusiveFirstMilePricePerCubicFt', label: 'All Inclusive First Mile Price Per ft³', slug: WarehouseSlugs.AllInclusiveFirstMilePricePerCubicFt, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'allInclusive' },
        { controlName: 'wsAllInclusiveStoragePricing', label: 'All Inclusive Storage Pricing', slug: WarehouseSlugs.AllInclusiveStoragePricing, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'allInclusive' },
        { controlName: 'wsArtworkInstallationFee', label: 'Artwork Installation Fee', slug: WarehouseSlugs.ArtworkInstallationFee, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'allInclusive' },
        { controlName: 'wsAllInclusiveDeliveryMinimumPrice', label: 'All Inclusive Delivery Minimum Price', slug: WarehouseSlugs.AllInclusiveDeliveryMinimumPrice, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'allInclusive' },
        { controlName: 'wsAllInclusivePickupMinimumPrice', label: 'All Inclusive Pickup Minimum Price', slug: WarehouseSlugs.AllInclusivePickupMinimumPrice, icon: 'heroicons_solid:currency-dollar', type: 'currency', section: 'allInclusive' },
        { controlName: 'wsAllInclusiveTrialDays', label: 'All Inclusive Trial Days', slug: WarehouseSlugs.AllInclusiveTrialDays, icon: 'calendar_today', type: 'integer', section: 'allInclusive', validators: [Validators.min(0)] },
    ]);
    
    readonly formChanged = signal(false);
    readonly websiteSettings = toSignal<Record<string, CustomWebsiteSettingsView> | null, Record<string, CustomWebsiteSettingsView>>(this.websiteSettingsViewService.currentWebsiteSettings$, { initialValue: {} });
    readonly projectStorageType = computed(() => this.dialogData?.storageType ?? this.projectView()?.storageType ?? null);
    readonly projectStorageChargeType = computed(() => this.dialogData?.storageChargeType ?? this.projectView()?.storageChargeType ?? null);
    readonly isAllInclusive = computed(() => this.projectStorageType() === StorageType.AllInclusive);
    readonly projectSettingForm = this.initForm();
    readonly isFormInvalid = signal(this.projectSettingForm.invalid);
    readonly saveDisabled = computed(() => this.isLoading() || !this.formChanged() || this.isFormInvalid());
    readonly generalFields = computed(() => this.formFields().filter((field) => field.section === 'general' && this.isFieldVisible(field)));
    readonly perServiceFields = computed(() => this.formFields().filter((field) => field.section === 'perService' && this.isFieldVisible(field)));
    readonly allInclusiveFields = computed(() => this.formFields().filter((field) => field.section === 'allInclusive' && this.isFieldVisible(field)));

    constructor() {
        effect(() => {
            this.projectStorageType();
            this.projectStorageChargeType();
            this.updateFieldStates();
        });

        this.projectSettingForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.syncFormInvalidState());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.projectId.set(this.route.snapshot.paramMap.get('projectId'));

        if (this.isDialogSetting() && this.dialogData?.websiteId) {
            this.isLoading.set(true);
            const request = new PageRequest();
            request.currentPage = 1;
            request.itemsPerPage = 1000;
            request.find['settingSlug:in'] = [...new Set(this.formFields().map((field) => field.slug))].join(',');
            request.find.websiteId = this.dialogData?.websiteId;
            request.noObserverEmit = true;
            this.websiteSettingsViewService.get(request)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => this.isLoading.set(false)),
                )
                .subscribe({
                    next: (response) => {
                        this.updateFormFields(response.items as CustomWebsiteSettingsView[]);
                        const patchData = this.buildDialogFormValues(response.items as CustomWebsiteSettingsView[]);
                        this.projectSettingForm.patchValue(patchData, { emitEvent: false });
                        this.updateFieldStates();
                    }
                });
        }

        this.projectSettingForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((currentValues: Record<string, ProjectSettingControlValue>) => {
                const originalValues = this.originalFormValues();
                const hasChanges = this.formFields().some((field) => {
                    if (!this.isFieldVisible(field) || this.isFieldReadonly(field)) {
                        return false;
                    }

                    const currentValue = this.projectSettingForm.get(field.controlName)?.value as ProjectSettingControlValue ?? currentValues[field.controlName] ?? null;
                    const originalValue = originalValues[field.controlName] ?? null;
                    return currentValue !== originalValue;
                });

                this.formChanged.set(hasChanges);
            });

            this.subscribeToProjectSettings();
        }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    submitProjectSettings(): void {
        if (this.projectSettingForm.invalid) {
            this.projectSettingForm.markAllAsTouched();
            this.alertService.errorToast('Form is invalid', 'Please fix these fields to continue');
            return;
        }

        const payload = this.getPayload();
        const requests = payload.items.map((item) => item.id ? this.projectSettingsService.patch(item.id, item) : this.projectSettingsService.create(item));
        if (!requests.length) return;

        forkJoin(requests)
            .pipe(
                switchMap(() => {
                    const request = new PageRequest();
                    request.currentPage = 1;
                    request.itemsPerPage = 1000;
                    request.find.projectId = this.projectId();
                    return this.projectSettingsViewService.get(request);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.alertService.successToast('Project settings updated successfully');
                },
                error: (error: HttpErrorResponse) => {
                    this.alertService.errorToast('Unable to save sidemark pricing', this.errorHandler(error));
                },
            });
    }

    buildSidemarkPricingConfig(): SidemarkPricingConfig {
        const configuredItems = this.getEditableVisibleFields().filter((field) => this.projectSettingForm.get(field.controlName)?.value != null);
        return {
            configured: this.projectSettingForm.valid && configuredItems.length > 0,
            items: configuredItems?.map((field) => ({
                ...field,
                value: this.convertToNumber(this.projectSettingForm.get(field.controlName)?.value as number | string | null),
                settingsId: field.settingsId ?? null,
            })),
        };
    }
    
    isFieldReadonly(field: ProjectSettingFormFieldConfig): boolean {
        return this.warehouseDeliveryFieldSlugs.has(field.slug);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private initForm(): FormGroup {
        const controls: Record<string, [ProjectSettingControlValue, ValidatorFn[]] | [ProjectSettingControlValue]> = {};

        for (const field of this.formFields()) {
            const validators = this.getFieldValidators(field);
            controls[field.controlName] = validators.length ? [null, validators] : [null];
        }

        return this.fb.group(controls);
    }

    private getFieldValidators(field: ProjectSettingFormFieldConfig): ValidatorFn[] {
        if (!this.isFieldVisible(field) || this.isFieldReadonly(field)) {
            return [];
        }

        const validators = field.validators ?? [];
        return validators.includes(Validators.required) ? validators : [Validators.required, ...validators];
    }

    private subscribeToProjectSettings(): void {
        if (this.isDialogSetting()) return;

        this.isLoading.set(true);
        this.projectSettingsViewService.observer$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                if (!response) {
                    this.isLoading.set(false);
                    return;
                }
                const patchData = this.buildProjectSettingsFormValues(response.items);
                this.projectSettings.set(response.items);
                this.updateFormFields(response.items);
                this.projectSettingForm.patchValue(patchData, { emitEvent: false });
                this.updateFieldStates();
                this.projectSettingForm.updateValueAndValidity({ emitEvent: false });
                this.originalFormValues.set({ ...patchData });
                this.formChanged.set(false);
                this.isLoading.set(false);
            });
    }

    private buildProjectSettingsFormValues(projectSettings: ProjectSettingsView[]): Record<string, number | null> {
        const patchData: Record<string, number | null> = {};

        for (const field of this.formFields()) {
            const projectSetting = projectSettings.find((item) => item.settingSlug === field.slug);
            const websiteSetting = this.websiteSettings()?.[field.slug];
            const fallbackValue = this.isFieldReadonly(field) ? websiteSetting?.settingsValue : null;

            patchData[field.controlName] = this.convertToNumber(projectSetting?.settingsValue ?? fallbackValue);
        }

        return patchData;
    }

    private buildDialogFormValues(items: CustomWebsiteSettingsView[]): Record<string, number | null> {
        const patchData: Record<string, number | null> = {};

        for (const field of this.formFields()) {
            const configuredValue = this.isFieldReadonly(field)
                ? null
                : this.dialogData?.config?.items.find((item) => item.slug === field.slug && (item.pricingModel ?? null) === (field.pricingModel ?? null))?.value;
            const websiteSetting = items.find((item) => this.isMatchingSetting(item, field));

            patchData[field.controlName] = typeof configuredValue === 'number'
                ? configuredValue
                : this.convertToNumber(websiteSetting?.settingsValue);
        }

        return patchData;
    }

    private updateFormFields(items: ProjectSettingsView[] | CustomWebsiteSettingsView[]): void {
        this.formFields.update((fields) =>
            fields.map((field) => {
                const item = items.find((current) => this.isMatchingSetting(current, field));
                if (!item) return field;

                return {
                    ...field,
                    label: 'settingName' in item ? item.settingName : item.name,
                    settingsId: item.settingsId ?? field.settingsId ?? null,
                };
            }),
        );
    }

    private getPayload(): BulkRequest<ProjectSetting> {
        const payload: BulkRequest<ProjectSetting> = { items: [] };

        this.getEditableVisibleFields().forEach((field) => {
            const control = this.projectSettingForm.get(field.controlName);
            if (!control) return;

            const currentProjectSetting = this.projectSettings().find((item) => item.settingSlug === field.slug);
            const settingValue = this.normalizeSettingValue(control.value as ProjectSettingControlValue);
            if (settingValue === null || currentProjectSetting?.settingsValue === settingValue) {
                return;
            }

            payload.items.push({
                settingsId: currentProjectSetting?.settingsId ?? this.websiteSettings()[field.slug]?.settingsId ?? null,
                id: currentProjectSetting?.id,
                settingsValue: settingValue.replace(/&nbsp;/g, ' '),
                projectId: this.projectId(),
            } as ProjectSetting);
        });

        return payload;
    }

    private getEditableVisibleFields(): ProjectSettingFormFieldConfig[] {
        return this.formFields().filter((field) => this.isFieldVisible(field) && !this.isFieldReadonly(field));
    }

    private isFieldVisible(field: ProjectSettingFormFieldConfig): boolean {
        switch (true) {
            case field.controlName === 'warehouseCoverageFeeOneTime':
                return this.projectStorageChargeType() === StorageChargeType.OneTime;

            case field.controlName === 'warehouseCoverageFee':
                return this.projectStorageChargeType() !== StorageChargeType.OneTime;

            case field.section === 'allInclusive':
                return this.isAllInclusive();

            case field.section === 'perService':
                if (this.isAllInclusive()) return false;
                if (this.squareFeetFieldSlugs.has(field.slug)) return this.projectStorageType() === StorageType.SquareFeet;
                if (this.cubicFeetFieldSlugs.has(field.slug)) return this.cubicStorageTypes.has(this.projectStorageType());
                return true;

            default:
                return true;
        }
    }

    private updateFieldStates(): void {
        for (const field of this.formFields()) {
            const control = this.projectSettingForm.get(field.controlName);
            if (!control) continue;

            const validators = this.getFieldValidators(field);
            control.setValidators(validators.length ? validators : null);

            if (!this.isFieldVisible(field) || this.isFieldReadonly(field)) {
                control.disable({ emitEvent: false });
            } else {
                control.enable({ emitEvent: false });
            }

            control.updateValueAndValidity({ emitEvent: false });
        }

        this.syncFormInvalidState();
    }

    private syncFormInvalidState(): void {
        this.isFormInvalid.set(this.projectSettingForm.invalid);
    }

    private isMatchingSetting(item: ProjectSettingsView | CustomWebsiteSettingsView, field: ProjectSettingFormFieldConfig): boolean {
        return item.settingSlug === field.slug
            && (('pricingModel' in item ? item.pricingModel : null) ?? null) === (field.pricingModel ?? null);
    }

    private normalizeSettingValue(value: ProjectSettingControlValue): string | null {
        if (Array.isArray(value)) {
            return value.join(', ');
        }

        if (typeof value === 'boolean') {
            return value ? SettingsValues.active : SettingsValues.inactive;
        }

        if (typeof value === 'number') {
            return value.toString();
        }

        if (value === null) {
            return null;
        }

        return value;
    }

    private convertToNumber(value?: string | number | null): number | null {
        const parsedValue = Number(value ?? NaN);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    private errorHandler(error: StorageAgreementHttpError): string {
        if (error.error?.ExceptionMessage) { 
            return error.error.ExceptionMessage;
        } else if (error.error?.title) {
            return error?.error?.title;
        } else {
            return 'An unexpected error occurred while saving sidemark pricing.';
        }
    }
}
